import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ApiService } from '../../../core/http/api.service';
import { UtilsService } from '../../../core/services/utils/utils.service';

@Component({
  selector: 'app-golden-handle-modal',
  templateUrl: './golden-handle-modal.component.html',
  styleUrls: ['./golden-handle-modal.component.css'],
  standalone: false,
})
export class GoldenHandleModalComponent {
  readonly profilePlaceholder = 'assets/images/user_avatar.png';
  private currentGoldenHandle: any = null;
  posts: any[] = [];
  loading = false;
  loadingMore = false;
  showScrollToTop = false;
  private page = 1;
  private totalPages = 1;
  private readonly pageSize = 10;
  expandedPostText = new Set<string>();
  truncatedPostText = new Set<string>();

  @Output() closed = new EventEmitter<void>();

  @Input() set goldenHandle(value: any) {
    this.currentGoldenHandle = value;
    this.loadPosts();
  }

  get goldenHandle(): any {
    return this.currentGoldenHandle;
  }

  constructor(
    private api: ApiService,
    private utils: UtilsService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  close(): void {
    this.closed.emit();
  }

  compactNumber(value: unknown): string {
    return Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(Number(value || 0));
  }

  profileWebsite(metadata: any): string {
    return metadata?.url || metadata?.entities?.url?.urls?.[0]?.expanded_url || '';
  }

  highResolutionProfilePicture(url: string): string {
    return String(url || '').replace(/_normal(?=\.[a-z0-9]+(?:\?|$))/i, '_400x400');
  }

  onProfileImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (!image.src.endsWith(this.profilePlaceholder)) image.src = this.profilePlaceholder;
  }

  isPostTextTruncated(key: string): boolean {
    return this.truncatedPostText.has(key);
  }

  togglePostText(key: string): void {
    if (this.expandedPostText.has(key)) this.expandedPostText.delete(key);
    else this.expandedPostText.add(key);
  }

  onModalScroll(event: Event): void {
    const element = event.target as HTMLElement;
    this.showScrollToTop = element.scrollTop > 300;
    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceFromBottom <= 200) this.loadNextPage();
  }

  scrollToTop(element: HTMLElement): void {
    element.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadPosts(): void {
    this.posts = [];
    this.showScrollToTop = false;
    this.page = 1;
    this.totalPages = 1;
    this.expandedPostText.clear();
    this.truncatedPostText.clear();
    const id = this.currentGoldenHandle?.id;
    if (!id) return;

    this.loading = true;
    this.fetchPosts(id, 1);
  }

  private loadNextPage(): void {
    const id = this.currentGoldenHandle?.id;
    if (!id || this.loading || this.loadingMore || this.page >= this.totalPages) return;
    this.loadingMore = true;
    this.fetchPosts(id, this.page + 1);
  }

  private fetchPosts(id: string, page: number): void {
    this.api
      .getTwitterGoldenListMemberPosts(id, { page, limit: this.pageSize })
      .subscribe(
      (res) => {
        if (res?.goldenHandle) {
          this.currentGoldenHandle = {
            ...this.currentGoldenHandle,
            ...res.goldenHandle,
          };
        }
        const nextPosts = Array.isArray(res?.data) ? res.data : [];
        this.posts = page === 1 ? nextPosts : [...this.posts, ...nextPosts];
        this.page = Number(res?.current_page || page);
        this.totalPages = Number(res?.totalPages || this.page);
        this.loading = false;
        this.loadingMore = false;
        requestAnimationFrame(() => this.measureTruncatedPostText());
      },
      (err) => {
        this.loading = false;
        this.loadingMore = false;
        this.utils.resErr(err);
      },
    );
  }

  private measureTruncatedPostText(): void {
    this.truncatedPostText.clear();
    this.elementRef.nativeElement
      .querySelectorAll<HTMLElement>('[data-post-text-key]')
      .forEach((element) => {
        if (element.scrollHeight <= element.clientHeight + 1) return;
        const key = element.dataset['postTextKey'];
        if (key) this.truncatedPostText.add(key);
      });
  }
}
